/**
 * Getting a fresh OAuth2 access token at send time.
 *
 * Refresh-before-send already existed in RequestBuilder, but only for a config
 * that HAS a refresh token. A `client_credentials` grant normally has none —
 * the spec says not to issue one for it, and most servers don't — so that
 * whole branch never fired for the grant that needs it most: the token simply
 * expired and every request 401'd until someone clicked "Get New Access Token"
 * by hand. Re-running the client-credentials exchange is the renewal for that
 * grant; it costs one machine-to-machine call and needs no user present.
 *
 * `authorization_code_pkce` without a refresh token is deliberately NOT
 * renewed here: renewing it means opening a browser window and waiting for a
 * human, which is not something to do silently in the middle of a send.
 */
import {
  refreshOAuthToken,
  runOAuthClientCredentials,
  isOAuthTokenExpired,
} from '../utils/oauthFlow';

/**
 * Does this config need — and permit — an unattended renewal right now?
 *
 * @param {object} oauth2
 * @returns {'refresh'|'client_credentials'|null}
 */
export function renewalKind(oauth2) {
  if (!oauth2 || !oauth2.tokenUrl) return null;
  if (oauth2.grantType === 'manual') return null;
  if (!isOAuthTokenExpired(oauth2)) return null;
  if (oauth2.refreshToken) return 'refresh';
  if (oauth2.grantType === 'client_credentials') return 'client_credentials';
  return null;
}

/**
 * In-flight renewals, keyed by the identity of the token being renewed.
 *
 * Without this, two tabs sending at once against the same expired config each
 * start their own exchange and the second one's result overwrites the first —
 * harmless with most servers, but some issue single-use refresh tokens and
 * invalidate the loser, which locks the user out until they re-authorise.
 */
const inFlight = new Map();

/** What makes two renewals "the same one". Never includes the secret's value. */
function renewalKey(oauth2) {
  return [oauth2.tokenUrl, oauth2.clientId, oauth2.grantType, oauth2.refreshToken ? 'r' : ''].join('|');
}

/**
 * Renew if needed. Returns the fields to merge into the config, or null when
 * nothing was due.
 *
 * Throws whatever the exchange threw — the caller decides how loudly to say so.
 *
 * @param {object} oauth2
 * @returns {Promise<object|null>}
 */
export async function renewOAuthToken(oauth2, { substituteVariables } = {}) {
  const kind = renewalKind(oauth2);
  if (!kind) return null;

  const key = renewalKey(oauth2);
  const existing = inFlight.get(key);
  if (existing) return existing;

  // `{{clientId}}` / `{{clientSecret}}` / `{{tokenUrl}}` are resolved for the
  // exchange only (utils/oauthFlow.resolveOAuthConfig); the stored config keeps them.
  const options = { substituteVariables };
  const run = (kind === 'refresh'
    ? refreshOAuthToken(oauth2, options)
    : runOAuthClientCredentials(oauth2, options))
    .finally(() => { inFlight.delete(key); });

  inFlight.set(key, run);
  return run;
}

export default renewOAuthToken;
